// THE MELEE ARC — one swing resolver for the whole game.
//
// Until now there were FOUR copies of this test, one per target collection:
//   src/world/dummies.js  meleeHit()   reach + 0.3, dot 0.45
//   src/world/animals.js  meleeHit()   reach + 0.4, dot 0.50
//   src/world/zombies.js  meleeHit()   reach + 0.5, dot 0.50
//   src/main.js           remoteHit()  reach + 0.4, dot 0.50
// Four copies that disagreed on how far a swing reaches and how wide its arc
// is, so the same move landed at different distances depending on WHAT it was
// aimed at. All four were flat 2D cones, which meant:
//   • no Y band — a swing on the ground floor hit the target standing on the
//     floor above, and a target in the cellar;
//   • no occlusion — a swing went straight through a wall, a shut door or a
//     tree, because nothing in the game could answer "is A→B blocked" until
//     src/world/query.mjs landed;
//   • no per-window memory — a target standing in two collections at once took
//     the same hit window twice, because each of the four copies re-ran the
//     cone from scratch with no shared memory. (A four-window combo landing
//     four times is NOT that defect: weapons.js tunes `dmg` per window and the
//     extra windows ARE the combo reward. See the memory below.)
// This module is those four copies, merged, with the three missing rules.
//
// WHY IT LIVES HERE. It is in src/world/ and not src/player/ because all four
// callers are world-side target collections, and because the occlusion half of
// the answer comes from src/world/query.mjs, which lives here too. It is .mjs,
// Babylon-free and import-free for exactly the reasons query.mjs and
// infected-sim.mjs are: that is the only way to prove it in plain Node
// (tools/meleetest.mjs), and this project has no browser in the verification
// loop. It is also why `playerPos` is read as {x, y, z} rather than as a
// Vector3 — a Babylon Vector3 satisfies that, so the callers pass theirs in
// unchanged.
//
// WHAT IT IS NOT. It is not a hitbox test and not a capsule sweep: it is the
// same cheap cone the four copies were, plus a y band plus one occlusion
// query. Precise geometry belongs to query.mjs ray(). It also does not apply
// damage, play sounds or count hits — every caller keeps its own damage
// contract, so the numbers a dummy, an animal, an infected and a remote
// player take are unchanged; the only hits that disappear are the ones that
// were reaching through a floor or a wall.

// ---------------------------------------------------------------------------
// THE ONE SET OF CONSTANTS. Each line records what was chosen and why, because
// three of these numbers used to exist in several versions at once.
// ---------------------------------------------------------------------------
export const MELEE = {
  // Reach tolerance, metres. The four copies used +0.3, +0.4, +0.4 and +0.5.
  // The middle value wins: two of the four already used it, and it keeps every
  // weapon's effective reach within 10 cm of what it is today (worst case the
  // infected, who lose 10 cm, and the dummies, who gain 10 cm). The pad exists
  // at all because both ends of this test are POINTS while a body is about
  // 0.7 m across, so a swing that visually connects with a shoulder is
  // measured centre-to-centre and would otherwise miss.
  reachPad: 0.4,

  // Arc, as the cosine of the half-angle: 0.45 is about ±63°. dummies.js used
  // 0.45, the other three 0.5 (±60°). The WIDER one wins, deliberately: 0.45
  // is the arc the melee feel was tuned against in the gym, and tightening it
  // would silently make every swing in the live game harder to land than it
  // was yesterday. Widening the other three by 3° is the smaller change.
  arcDot: 0.45,

  // Y band, metres, between the attacker's feet and the target's feet. This is
  // the rule none of the four copies had. towns.json floorHeight is 2.8 m, so
  // 1.2 m cannot reach the storey above or below, while still covering a
  // target on a kerb, a stair tread or a 1 m ledge.
  yBand: 1.2,

  // …but on a 10 km map almost nothing is level, and a flat band this tight
  // starts refusing swings that visually connect. MEASURED against the real
  // height field, full-reach swings over land: 14.9 % rejected with a knife,
  // 18.9 % with fists, 22.0 % with a bat — because 37 % of the land is steeper
  // than the 0.43 gradient it takes to put 1.2 m between two pairs of feet
  // 2.8 m apart. A swing that should land and does not is the worst thing this
  // module can do, so a height difference the GROUND ITSELF explains is
  // allowed up to yBandSlope: both bodies standing on the surface where they
  // are means they are on the same hillside, not on two storeys. That takes
  // the bat's rejection rate to 9.1 % — the rest is genuinely too steep to
  // reach across.
  //
  // yBandSlope stays well under floorHeight so the storey rule survives: a
  // target one floor up is 2.8 m away vertically and still refused, and a
  // target on a mezzanine is refused by the footing test instead (their feet
  // are not on the surface the ground function reports under them).
  yBandSlope: 2.3,
  // How far a body's feet may be off the reported ground and still count as
  // standing on it — a jump, a stair tread, a slope sampled a metre away.
  yFoot: 0.5,

  // The vertical band the swing itself travels through, metres above the
  // attacker's feet, used for the occlusion segment. 0.4 is low enough that a
  // waist-high wall or a car bonnet stops the swing; 1.7 is high enough that a
  // window lintel or a doorway head above you does not. A swing is not a
  // bullet: it has a body, and this is that body.
  occLow: 0.4,
  occHigh: 1.7,
};

// ---------------------------------------------------------------------------
// PER-HIT-WINDOW HIT MEMORY.
//
// What this memory is for: ONE hit window opens four resolvers back to back
// (dummies.js, animals.js, zombies.js and main.js each build their own swing),
// so a target that appears in two collections must not take the hit twice.
//
// What it must NOT do is swallow a combo. It used to key on the identity of
// controller.js's `attack` object, which is ONE object per MOVE carrying every
// hit window (controller.js tryAttack / tryGunMelee build it, update() fires
// the windows out of it), so a target could only be hit once per move. But
// weapons.js tunes `dmg` PER WINDOW and uses the extra windows as the combo
// reward — 'quad-punch' is dmg 8 × hits [0.2, 0.4, 0.6, 0.8] against a single
// 'jab' of dmg 8, 'melee-combo-2' dmg 18 × 3 against a single 'melee-
// horizontal' of 22 — so keying on the move cost Quad Punch 32 → 8 damage,
// bat Swing Combo 54 → 18, Two-Hand Combo 40 → 20 and One-Two 20 → 10, with
// no patch note and no UI signal. A chain ≥ 2 finisher at 8 total damage would
// be weaker than the jab it chains off.
//
// So the key is the WINDOW, derived without touching src/player/: controller
// sets `h.done = true` BEFORE it runs the resolvers, so the number of done
// windows is constant across one window's four resolvers and increments on the
// next one. The pair (attack object, done count) therefore names the window
// exactly, with no new signal threaded through the controller.
//
// If there is no attack object to read (a unit test, the gym before the
// controller exists), the memory is simply cleared on every call, which is
// exactly the old behaviour — the guard degrades to nothing rather than to a
// target that can never be hit again.
// ---------------------------------------------------------------------------
let swingKey = null;
let swingWin = -1;
const swingHit = new Set();

// How many of this move's hit windows have already fired. Counting is cheap
// (a move has at most four windows) and needs no cooperation from the caller.
function windowOf(a) {
  const hits = a && a.hits;
  if (!Array.isArray(hits)) return 0;
  let n = 0;
  for (let i = 0; i < hits.length; i++) if (hits[i] && hits[i].done) n++;
  return n;
}

function beginSwing(world) {
  const key = (world && world.player && world.player.attack) || null;
  if (key === null) { swingKey = null; swingWin = -1; swingHit.clear(); return false; }
  const win = windowOf(key);
  if (key !== swingKey || win !== swingWin) {
    swingKey = key; swingWin = win; swingHit.clear();
  }
  return true;
}

// Test hook: forget the current swing. Only tools/meleetest.mjs uses this.
export function resetSwing() { swingKey = null; swingWin = -1; swingHit.clear(); }

function fin(n) { return typeof n === 'number' && Number.isFinite(n); }

// The occlusion segment runs from the attacker's FEET to the target's FEET,
// and every pushOut() in the project parks a body exactly on its collider's
// face — with props.js and buildings.js storing a 0.30-0.35 m movement skin
// around every car, crate, fence and sealed building on top of that. A closed
// segment test therefore called a resting contact a block: back to a wall was
// total melee immunity, and cornering someone stopped your own swings. Asking
// the query to ignore a collider that CONTAINS one of the two bodies is the
// honest question here — "what is BETWEEN us" — and it is opt-in precisely
// because movement must never ask it. Hoisted so a swing allocates nothing.
const OPEN_ENDS = { openEnds: true };

// The resolver handed back when the attacker's own position is unusable.
const MISS = { test() { return false; } };

/**
 * Open a swing against one target collection.
 *
 * @param world      the shared world object: `world.query` supplies the
 *                   occlusion answer and `world.player.attack` the swing id.
 *                   Both are optional — with neither, this is the old cone
 *                   plus the y band.
 * @param playerPos  {x, y, z} of the attacker's FEET (root.position).
 * @param heading    radians, the controller's `heading`.
 * @param reach      the move's reach in metres (weapons.js).
 * @returns { test(x, y, z, id) -> boolean }
 */
export function meleeSwing(world, playerPos, heading, reach) {
  const dedupe = beginSwing(world);
  const px = playerPos.x, py = playerPos.y, pz = playerPos.z;
  // Non-finite input misses, and misses on PURPOSE rather than by accident.
  // NaN fails every `>` comparison below, so without this an attacker or a
  // target with a NaN coordinate would sail through the distance test, the arc
  // test and the y band and land a hit. query.segmentBlocked() happens to
  // catch it (it returns TRUE for non-finite input) but only when a query is
  // wired at all — in the gym there is none. Of the two ways to be wrong,
  // dropping a swing is invisible and damage out of nowhere is not.
  const live = fin(px) && fin(py) && fin(pz) && fin(heading) && fin(reach);
  const fx = Math.sin(heading), fz = Math.cos(heading);
  const maxD = reach + MELEE.reachPad;
  const maxD2 = maxD * maxD;
  const query = world && world.query;
  const y0 = py + MELEE.occLow, y1 = py + MELEE.occHigh;
  // OPTIONAL. main.js installs it in map mode (terrain height, overridden by
  // an interior floor slab); the gym has none, and there the flat band is
  // exactly right because the gym floor is flat.
  const groundY = world && typeof world.getGroundY === 'function' ? world.getGroundY : null;
  // Is this body standing on the surface the world reports under it? Used only
  // to tell a hillside from a storey, so it is asked only when the plain band
  // has already been exceeded.
  const footed = (fx2, fy2, fz2) => {
    const g = groundY(fx2, fz2);
    return fin(g) && Math.abs(fy2 - g) <= MELEE.yFoot;
  };

  if (!live) return MISS;

  return {
    /**
     * Can this swing legitimately land on a target whose FEET are at
     * (x, y, z)? `id` is any Set key — the target object itself is what all
     * four callers pass, which is both free and collision-proof.
     *
     * Marks the target as hit on success, so this returns true at most once
     * per target per swing.
     */
    test(x, y, z, id) {
      if (!fin(x) || !fin(y) || !fin(z)) return false;
      const dx = x - px, dz = z - pz;
      const d2 = dx * dx + dz * dz;
      if (d2 > maxD2) return false;
      const d = Math.sqrt(d2);
      // Dividing by `d || 1` keeps the degenerate case the four copies had:
      // a target standing exactly on the attacker scores dot 0 and is missed.
      if ((dx * fx + dz * fz) / (d || 1) < MELEE.arcDot) return false;
      const dy = Math.abs(y - py);
      if (dy > MELEE.yBand) {
        // Beyond the flat band, the only acceptable reason is the ground.
        if (dy > MELEE.yBandSlope || !groundY) return false;
        if (!footed(px, py, pz) || !footed(x, y, z)) return false;
      }
      // Cheap tests first, then memory, then occlusion: the segment query is
      // the only part that touches the collider hashes (a handful of slab
      // tests over 2 m of reach, ~2 us), and almost nothing survives to it.
      if (dedupe && swingHit.has(id)) return false;
      if (query && query.segmentBlocked(px, pz, x, z, y0, y1, OPEN_ENDS)) return false;
      if (dedupe) swingHit.add(id);
      return true;
    },
  };
}
